import type { Navigator } from "@solidjs/router";

export type NavState = {
  /** Preferred path when history.back is unavailable */
  from?: string;
};

/**
 * Overlays (full-screen editor) register here so the Telegram BackButton and
 * any other goBack() call close the overlay first instead of leaving the page.
 */
const backInterceptors: Array<() => void> = [];

export function pushBackInterceptor(fn: () => void): () => void {
  backInterceptors.push(fn);
  return () => {
    const i = backInterceptors.lastIndexOf(fn);
    if (i >= 0) backInterceptors.splice(i, 1);
  };
}

/**
 * Safe back navigation for Telegram Mini App deep links.
 * Prefer real history when available; otherwise fall back to `fallback`
 * (default: calendar `/`, not the workouts list — avoids the old "trap").
 */
export function goBack(navigate: Navigator, fallback = "/") {
  const top = backInterceptors[backInterceptors.length - 1];
  if (top) {
    top();
    return;
  }
  try {
    const state = window.history.state as {
      idx?: number;
      _index?: number;
    } | null;
    const idx = state?.idx ?? state?._index;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
      return;
    }
    // history.length > 1 is a weak signal but better than stuck screens
    if (window.history.length > 1 && document.referrer) {
      const refHost = (() => {
        try {
          return new URL(document.referrer).host;
        } catch {
          return "";
        }
      })();
      if (refHost && refHost === window.location.host) {
        navigate(-1);
        return;
      }
    }
  } catch {
    /* fall through */
  }
  navigate(fallback || "/");
}

/** Read `from` navigation state set by list/calendar links. */
export function backFallbackFromState(
  state: unknown,
  defaultFallback = "/",
): string {
  if (
    state &&
    typeof state === "object" &&
    "from" in state &&
    typeof (state as NavState).from === "string" &&
    (state as NavState).from!.startsWith("/")
  ) {
    return (state as NavState).from!;
  }
  return defaultFallback;
}

/** Build location state so detail "Back" returns to the right screen. */
export function withFrom(from: string): NavState {
  return { from };
}
